import {Injectable, NgZone} from '@angular/core';

/**
 * Tablas legibles en el celular, sin tocar cada pantalla.
 *
 * Observa el DOM y a cada tabla de listado (`table.mat-table` y las de bootstrap `table.table-hover`)
 * le pone una de dos clases:
 *  - `tabla-tarjetas`: hasta MAX_COLUMNAS columnas. En 600px o menos cada fila se muestra como
 *    tarjeta y cada celda lleva su etiqueta (`data-label`, tomada de la cabecera de su columna).
 *  - `tabla-ancha`: matrices con muchas columnas (por ejemplo, un dia por columna). Se quedan como
 *    tabla y se desplazan de lado dentro de su tarjeta.
 * Una tabla puede pedir quedarse como tabla con la clase `tabla-sin-tarjetas`.
 * Los estilos estan en `src/assets/scss/_responsive-admin.scss`.
 *
 * De paso, a los botones que solo muestran un icono y no tienen nombre les pone `aria-label` y
 * `title` segun el icono (add -> «Agregar», refresh -> «Actualizar»...), para lectores de pantalla
 * y para que al pasar el cursor se sepa que hacen.
 */
@Injectable({providedIn: 'root'})
export class TablaResponsiveService {

    private static readonly MAX_COLUMNAS = 12;

    /** Nombre de la accion segun el icono de Material del boton. */
    private static readonly NOMBRE_ICONO: { [icono: string]: string } = {
        add: 'Agregar', add_circle: 'Agregar', refresh: 'Actualizar', autorenew: 'Actualizar',
        edit: 'Editar', create: 'Editar', delete: 'Eliminar', delete_forever: 'Eliminar', delete_outline: 'Eliminar',
        visibility: 'Ver', remove_red_eye: 'Ver', search: 'Buscar', close: 'Cerrar', clear: 'Limpiar',
        file_download: 'Descargar', download: 'Descargar', cloud_download: 'Descargar', get_app: 'Descargar',
        file_upload: 'Subir archivo', cloud_upload: 'Subir archivo', upload: 'Subir archivo', attach_file: 'Adjuntar',
        print: 'Imprimir', mail: 'Enviar correo', email: 'Enviar correo', send: 'Enviar', save: 'Guardar',
        check: 'Aprobar', done: 'Aprobar', thumb_up: 'Aprobar', thumb_down: 'Rechazar', block: 'Desactivar',
        alarm: 'Horario', settings: 'Configurar', more_vert: 'Más opciones', filter_list: 'Filtrar',
        content_copy: 'Copiar', history: 'Historial', lock: 'Cerrar', lock_open: 'Reabrir', person_add: 'Agregar persona',
        description: 'Ver documento', picture_as_pdf: 'Ver PDF', menu: 'Menú', arrow_back: 'Volver'
    };

    private observador: MutationObserver = null;
    private pendiente: any = null;

    constructor(private zone: NgZone) {
    }

    iniciar(): void {
        if (this.observador || typeof MutationObserver === 'undefined') {
            return;
        }
        // fuera de Angular: marcar celdas no debe disparar deteccion de cambios
        this.zone.runOutsideAngular(() => {
            this.observador = new MutationObserver(() => this.programar());
            this.observador.observe(document.body, {childList: true, subtree: true});
            this.programar();
        });
    }

    private programar(): void {
        if (this.pendiente) {
            return;
        }
        this.pendiente = setTimeout(() => {
            this.pendiente = null;
            document.querySelectorAll('table.mat-table').forEach(t => this.preparar(t as HTMLTableElement));
            document.querySelectorAll('table.table.table-hover').forEach(t => this.prepararSimple(t as HTMLTableElement));
            document.querySelectorAll('button:not([aria-label])').forEach(b => this.nombrarBoton(b as HTMLButtonElement));
        }, 150);
    }

    private preparar(tabla: HTMLTableElement): void {
        const etiquetas: { [columna: string]: string } = {};
        const cabeceras = tabla.querySelectorAll('th.mat-header-cell');
        cabeceras.forEach(th => {
            const columna = this.columnaDe(th);
            if (columna) {
                etiquetas[columna] = (th.textContent || '').replace(/\s+/g, ' ').trim();
            }
        });

        const ancha = tabla.classList.contains('tabla-sin-tarjetas') || cabeceras.length > TablaResponsiveService.MAX_COLUMNAS;
        this.alternar(tabla, 'tabla-ancha', ancha);
        this.alternar(tabla, 'tabla-tarjetas', !ancha && cabeceras.length > 0);
        if (ancha) {
            return;
        }

        tabla.querySelectorAll('td.mat-cell').forEach(td => {
            const columna = this.columnaDe(td);
            const etiqueta = columna ? (etiquetas[columna] || '') : '';
            if (td.getAttribute('data-label') !== etiqueta) {
                td.setAttribute('data-label', etiqueta);
            }
            const vacia = (td.textContent || '').trim() === ''
                && !td.querySelector('button, a, img, input, mat-icon, mat-checkbox, mat-slide-toggle, .badge, i');
            this.alternar(td, 'celda-vacia', vacia);
            this.alternar(td, 'celda-acciones', /actions|acciones|opciones/i.test(columna || ''));
        });
    }

    /**
     * Tablas de bootstrap: no tienen clases por columna, asi que la etiqueta sale de la posicion
     * (ultima fila de cabecera, respetando colspan). Las celdas que abarcan varias columnas
     * (mensajes de «sin registros») no llevan etiqueta.
     */
    private prepararSimple(tabla: HTMLTableElement): void {
        if (tabla.classList.contains('tabla-sin-tarjetas') || tabla.closest('.mat-dialog-container')) {
            this.alternar(tabla, 'tabla-tarjetas', false);
            return;
        }
        const filasCabecera = tabla.tHead ? tabla.tHead.rows : null;
        if (!filasCabecera || filasCabecera.length === 0) {
            return;
        }
        const etiquetas: string[] = [];
        Array.from(filasCabecera[filasCabecera.length - 1].cells).forEach(th => {
            const texto = (th.textContent || '').replace(/\s+/g, ' ').trim();
            for (let i = 0; i < (th.colSpan || 1); i++) {
                etiquetas.push(texto);
            }
        });
        const ancha = etiquetas.length > TablaResponsiveService.MAX_COLUMNAS;
        this.alternar(tabla, 'tabla-ancha', ancha);
        this.alternar(tabla, 'tabla-tarjetas', !ancha);
        if (ancha) {
            return;
        }
        Array.from(tabla.tBodies).forEach(cuerpo => Array.from(cuerpo.rows).forEach(fila => {
            let posicion = 0;
            Array.from(fila.cells).forEach(td => {
                const etiqueta = td.colSpan > 1 ? '' : (etiquetas[posicion] || '');
                posicion += td.colSpan || 1;
                if (td.getAttribute('data-label') !== etiqueta) {
                    td.setAttribute('data-label', etiqueta);
                }
                const vacia = (td.textContent || '').trim() === ''
                    && !td.querySelector('button, a, img, input, mat-icon, mat-checkbox, mat-slide-toggle, .badge, i');
                this.alternar(td, 'celda-vacia', vacia);
                this.alternar(td, 'celda-acciones', /acci[oó]n|opciones/i.test(etiqueta)
                    || (!!td.querySelector('button') && (td.textContent || '').trim() === ''));
            });
        }));
    }

    /** Pone nombre a un boton que solo tiene icono, si el icono es conocido. */
    private nombrarBoton(boton: HTMLButtonElement): void {
        const icono = boton.querySelector('mat-icon, i.material-icons');
        if (!icono) {
            return;
        }
        const ligadura = (icono.textContent || '').trim();
        const textoPropio = (boton.textContent || '').replace(ligadura, '').trim();
        if (textoPropio !== '') {
            return; // ya tiene texto visible
        }
        const nombre = TablaResponsiveService.NOMBRE_ICONO[ligadura];
        if (!nombre) {
            return;
        }
        boton.setAttribute('aria-label', nombre);
        if (!boton.getAttribute('title') && !boton.hasAttribute('mattooltip') && !boton.hasAttribute('ng-reflect-message')) {
            boton.setAttribute('title', nombre);
        }
    }

    /** Nombre de la columna a partir de la clase `mat-column-xxx` que pone Angular Material. */
    private columnaDe(celda: Element): string {
        for (let i = 0; i < celda.classList.length; i++) {
            const clase = celda.classList.item(i);
            if (clase.indexOf('mat-column-') === 0) {
                return clase.substring('mat-column-'.length);
            }
        }
        return null;
    }

    private alternar(el: Element, clase: string, activa: boolean): void {
        if (el.classList.contains(clase) !== activa) {
            el.classList.toggle(clase, activa);
        }
    }
}
